import { DubMeasured, dubSoundingMs, tapeOrder, wholeMs } from "./dubSounding";

/**
 * «Без автоправок»: наезды озвучек, лежащие в файлах.
 *
 * В этом режиме редактор рисует озвучки там, где их положили файлы (раскладка при показе —
 * adjustAllFragments — отключена), и загрузка ничего не двигает и не сохраняет. Наложений при
 * этом не бывает (правило владельца 01.10.2026 «ты должен всегда сдвигать соседние реплики
 * иначе будут наслоения»): следующая правка в папке (cascadeNeighbours в провайдере, проход
 * сервера после озвучки) отодвигает следующую озвучку позже — на конец этой + 50 мс, а
 * размещение сборки ставит её звук ровно на этот конец. Метка — чтобы переводчик видел это
 * заранее, на экране, и мог поставить озвучки сам. Допуска нет: все три прохода толкают
 * наложение от 1 мс (место раньше конца предыдущего дубля), значит, и метится каждое.
 */

interface Placed extends DubMeasured {
    id?: string;
    start: number;
}

/**
 * Каждой озвучке, хвост которой заходит на следующую озвучку папки (хоть на 1 мс), ставит
 * `manual_overlap_ms` — на сколько. Метка у той, что раньше: её хвост рисуется поверх соседки
 * (см. `trimmed` в prepareDataOne), значит, и красное видно именно на нём. Реплики без wav
 * ничего не звучат и в наездах не участвуют — как и в сборке. Сколько звучит озвучка и
 * озвучена ли она — мерка раздвижки и сервера (`dubSoundingMs`: `dub_ms`, у старого сервера —
 * `file` и `length`), места — в целых мс, лента — в порядке прохода (`tapeOrder`: место как
 * есть, затем id): метка и сдвиг не должны расходиться. Порядок и остальные поля не меняются;
 * ставится метка только на копии.
 */
export function markManualOverlaps<T extends Placed>(tracks: T[]): (T & { manual_overlap_ms?: number })[] {
    const voiced = tracks
        .map((t) => ({ t, sounding: dubSoundingMs(t) }))
        .filter((v): v is { t: T; sounding: number } => v.sounding !== null)
        .sort((a, b) => tapeOrder(a.t, b.t));
    const overlapOf = new Map<T, number>();
    for (let i = 0; i + 1 < voiced.length; i++) {
        const overlap = wholeMs(voiced[i].t.start) + voiced[i].sounding - wholeMs(voiced[i + 1].t.start);
        if (overlap > 0) overlapOf.set(voiced[i].t, Math.round(overlap));
    }
    return tracks.map((t) => (overlapOf.has(t) ? { ...t, manual_overlap_ms: overlapOf.get(t) } : t));
}
